import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  CreateUserInput,
  DataEnvelope,
  ListEnvelope,
  Role,
  AgentStatus,
  UpdateUserInput,
  User,
} from './types';

export interface UserFilters {
  q?: string;
  role?: Role | '';
  status?: AgentStatus | '';
  teamId?: string;
  isActive?: boolean;
}

export const usersKey = (filters: UserFilters = {}) => ['users', filters] as const;

export function useUsersInfinite(filters: UserFilters) {
  return useInfiniteQuery({
    queryKey: [...usersKey(filters), 'infinite'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api<ListEnvelope<User>>('/users', { query: { ...filters, limit: 25, cursor: pageParam }, signal }),
    getNextPageParam: (last) => last.pagination.nextCursor ?? undefined,
  });
}

/** All active users (for pickers and name lookups). Teams are small; 100 covers Kollabr's support org. */
export function useAllUsers() {
  return useQuery({
    queryKey: usersKey({ isActive: true }),
    queryFn: ({ signal }) =>
      api<ListEnvelope<User>>('/users', { query: { limit: 100, isActive: true }, signal }).then(
        (r) => r.data,
      ),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      api<DataEnvelope<User>>('/users', { method: 'POST', body: input }).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      api<DataEnvelope<User>>(`/users/${id}`, { method: 'PATCH', body: input }).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}
